// src/features/self-assessment/hooks/useSelfAssessmentSession.js
//
// Implementa la macchina a stati del percorso pubblico SA
// (Specifica v1.1 §5.1: Apertura, Consenso, Conversazione, Validazione,
// Sospensione, Completamento, Token non valido, Errore temporaneo).
//
// Regole applicate:
// - /sa/open chiamato UNA sola volta per caricamento (§4.3); token rimosso
//   dalla URL dopo apertura valida con history.replaceState (§4.3)
// - UAT-11: il link dell'email riapre la stessa sessione finché è aperta;
//   con ripresa: true si torna alla chat (consenso già dato) con la storia
// - closes_session è l'UNICO segnale autoritativo di chiusura (§6.2)
// - in caso di errore di persistenza, il testo non inviato resta editabile
//   e la stessa domanda viene riproposta (§6.2)
// - nessun log di token o contenuti delle risposte (§4.3)

import { useCallback, useEffect, useRef, useState } from 'react';
import { normalizeMessaggio } from '../../../api/dto.js';

/** @typedef {'loading'|'consent'|'chatting'|'completed'|'invalid_token'|'expired'|'fatal_error'} SaPhase */

export function useSelfAssessmentSession(kmClient, { token, sessionIdParam }) {
  const [phase, setPhase] = useState('loading');
  const [messages, setMessages] = useState([]); // { id, role: 'bot'|'user', text, quickReply, isError }
  const [pendingText, setPendingText] = useState(''); // testo non inviato, riproposto su errore
  const [isSending, setIsSending] = useState(false);
  const [sessionId, setSessionId] = useState(sessionIdParam ?? null);
  const [fatalMessage, setFatalMessage] = useState('');
  // Aggiunto 23/09: { domande_attive, risposte_confermate, percentuale,
  // obbligatorie_mancanti, completabile } — da meta.progresso della
  // risposta reale (orchestrator.py, verificato nel sorgente). Assente sul
  // primo messaggio di apertura (/sa/open non lo include), valorizzato dal
  // primo giro di /sa/message in poi.
  const [progresso, setProgresso] = useState(null);
  const hasOpenedRef = useRef(false);

  const appendBotMessage = useCallback((normalized) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `bot-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        role: 'bot',
        text: normalized.text,
        quickReply: normalized.quickReply,
        griglia: normalized.griglia,
        selezione: normalized.selezione,
      },
    ]);
    if (normalized.closesSession) {
      setPhase('completed');
    }
  }, []);

  const appendUserMessage = useCallback((text) => {
    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}`, role: 'user', text },
    ]);
  }, []);

  const appendErrorMessage = useCallback((text) => {
    setMessages((prev) => [
      ...prev,
      { id: `err-${Date.now()}`, role: 'bot', text, isError: true },
    ]);
  }, []);

  // Apertura — chiamata una sola volta
  useEffect(() => {
    if (hasOpenedRef.current) return;
    hasOpenedRef.current = true;

    if (!token) {
      setPhase('invalid_token');
      return;
    }

    (async () => {
      try {
        const raw = await kmClient.post('/sa/open', undefined, {
          query: { token },
          isPublic: true,
          // 01/10: /sa/open ora apre il Self-Assessment conversazionale
          // (conversazione_sa.apertura, LLM per formulare l'invito) quando
          // KM_SA_MODALITA=conversazionale (default): col modello locale
          // osservati 50-75s a chiamata, ben oltre i 30s di
          // TIMEOUT_STANDARD_MS. Senza questo flag il fetch va in abort
          // prima che il backend risponda.
          isLlmOperation: true,
        });
        // Token consumato con successo: rimuoverlo dalla barra indirizzi (§4.3)
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        window.history.replaceState({}, '', url.toString());

        if (raw?.session_id) setSessionId(raw.session_id);
        // Corretto 18/09 dopo verifica dal vivo: la risposta di /sa/open
        // avvolge il messaggio dentro "risposta" — { session_id, jd_titolo,
        // risposta: { text, attachments, ... } } — diversa da /sa/message,
        // che restituisce questi campi direttamente al livello superiore.
        // Prima normalizeMessaggio(raw) cercava raw.text, che con questa
        // risposta non esiste mai: il primissimo messaggio (consenso GDPR)
        // sarebbe arrivato vuoto, senza i bottoni Accetto/Non accetto.
        const normalized = normalizeMessaggio(raw?.risposta ?? raw);
        const apertura = { id: 'bot-open', role: 'bot', text: normalized.text, quickReply: normalized.quickReply, griglia: normalized.griglia, selezione: normalized.selezione };
        // UAT-11: riapertura dello stesso link a conversazione avviata. Il
        // consenso è già stato dato: si va direttamente alla chat, con i
        // messaggi già scambiati (storia: [{ ruolo, testo }]) prima del punto
        // da cui si riprende.
        if (raw?.ripresa) {
          const storia = Array.isArray(raw.storia) ? raw.storia : [];
          setMessages([
            ...storia
              .filter((m) => m && m.testo)
              .map((m, i) => ({
                id: `storia-${i}`,
                role: m.ruolo === 'user' ? 'user' : 'bot',
                text: m.testo,
              })),
            apertura,
          ]);
          setPhase('chatting');
          return;
        }
        setMessages([apertura]);
        setPhase('consent');
      } catch (err) {
        if (err?.status === 403) {
          setPhase('invalid_token');
        } else {
          setFatalMessage(err?.detail || 'Il servizio non è raggiungibile in questo momento. Riprovare più tardi.');
          setPhase('fatal_error');
        }
      }
    })();
  }, [kmClient, token]);

  const sendMessage = useCallback(
    async (text, { replyId } = {}) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return;

      appendUserMessage(trimmed);
      setPendingText('');
      setIsSending(true);
      if (phase === 'consent') setPhase('chatting');

      try {
        const raw = await kmClient.post(
          '/sa/message',
          { session_id: sessionId, text: trimmed, reply_id: replyId ?? null },
          {
            isPublic: true,
            // 01/10: ogni turno del SA conversazionale incatena più
            // chiamate LLM lato backend (estrazione multi-campo +
            // formulazione del turno; fino a 4 sull'ultimo turno con
            // career summary e posizioni coerenti) — osservati 50-75s col
            // modello locale. Senza isLlmOperation il client abortiva a
            // 30s (TIMEOUT_STANDARD_MS) e mostrava "Non è stato possibile
            // salvare la risposta", anche quando il backend aveva
            // comunque salvato tutto.
            isLlmOperation: true,
          }
        );
        const normalized = normalizeMessaggio(raw);
        if (normalized.progresso) setProgresso(normalized.progresso);
        appendBotMessage(normalized);
      } catch (err) {
        if (err?.status === 422) {
          // Risposta non valida: mantenere la domanda, mostrare il formato atteso (§5.1)
          appendErrorMessage(err.detail || 'Il formato della risposta non è quello atteso. Riprova.');
          setPendingText(trimmed);
        } else if (err?.status === 403) {
          setPhase('expired');
        } else {
          appendErrorMessage(
            'Non è stato possibile salvare la risposta. Il testo non è andato perso: riprova a inviarlo.'
          );
          setPendingText(trimmed); // mantiene il testo non inviato (§9.1, errore temporaneo)
        }
      } finally {
        setIsSending(false);
      }
    },
    [kmClient, sessionId, phase, isSending, appendUserMessage, appendBotMessage, appendErrorMessage]
  );

  const consent = useCallback(
    (accepted) => {
      if (!accepted) {
        setPhase('expired');
        appendBotMessage({
          text: 'Nessun problema. Se cambi idea, puoi chiedere a HR un nuovo link in qualsiasi momento.',
          closesSession: false,
        });
        return;
      }
      // Corretto 23/09: "consent_yes" non è mai riconosciuto dal backend
      // (orchestrator.py, _handle_consent) — controlla PRIMA reply_id, poi
      // il testo solo se reply_id è vuoto. Un reply_id fuori da
      // {CONSENT_ACCEPT, CONSENT_REJECT, "accetto","si","sì","no",
      // "non accetto"} fa ripetere la richiesta di consenso da capo, anche
      // se il testo sarebbe stato corretto — verificato dal vivo, causa
      // del "mi chiede due volte la stessa cosa". "gdpr:accetto" è
      // CONSENT_ACCEPT, il valore esatto letto dal sorgente.
      sendMessage('Accetto.', { replyId: 'gdpr:accetto' });
    },
    [sendMessage, appendBotMessage]
  );

  return {
    phase,
    messages,
    pendingText,
    setPendingText,
    isSending,
    fatalMessage,
    progresso,
    sendMessage,
    consent,
  };
}
